/**
 * Story State Sync — persists past_story_viewed / free_story_used to MongoDB by device_id.
 * Survives app updates, reinstalls, localStorage clears.
 *
 * Usage:
 *   import { markPastStoryViewed, syncStoryStateFromServer } from '@/utils/storyState';
 *   markPastStoryViewed();              // sets LS + POSTs to backend
 *   await syncStoryStateFromServer();   // GET from backend on app load, merge into LS
 */

const API = (typeof window !== 'undefined' && process.env.REACT_APP_BACKEND_URL) || '';

/**
 * Resolve a stable device identifier.
 *
 * Priority order (most reinstall-proof first):
 *   1) Native Capacitor Device.getId()   → iOS IDFV / Android ANDROID_ID
 *      Survives app UNINSTALL + REINSTALL on the same device (industry standard).
 *   2) Capacitor Preferences              → survives localStorage clears on native webview
 *   3) localStorage device_id             → web & fallback
 *   4) Freshly generated UUID             → first-ever visit
 *
 * Once resolved, the id is mirrored into ALL three stores so subsequent
 * lookups stay consistent even if one is wiped.
 */
export async function getDeviceId() {
  let id = null;

  // 1) Try native device UUID (iOS IDFV / Android ID) — persists across reinstalls
  try {
    if (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) {
      const { Device } = await import('@capacitor/device');
      const info = await Device.getId();
      if (info?.identifier) id = `native-${info.identifier}`;
    }
  } catch (e) {}

  // 2) Capacitor Preferences (iOS UserDefaults / Android SharedPreferences)
  if (!id) {
    try {
      const pref = await window.Capacitor?.Plugins?.Preferences?.get?.({ key: 'device_id' });
      if (pref?.value) id = pref.value;
    } catch (e) {}
  }

  // 3) localStorage
  if (!id) {
    try {
      id = localStorage.getItem('device_id') || localStorage.getItem('taraanaya_device_id') || null;
    } catch (e) {}
  }

  // 4) Generate fresh
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  // Mirror into every store so subsequent reads stay consistent
  try { localStorage.setItem('device_id', id); localStorage.setItem('taraanaya_device_id', id); } catch (e) {}
  try { window.Capacitor?.Plugins?.Preferences?.set?.({ key: 'device_id', value: id }); } catch (e) {}

  return id;
}

/** Push current LS flags to server. Fire-and-forget; does NOT block UI. */
export async function pushStoryStateToServer(overrides = {}) {
  const device_id = await getDeviceId();
  if (!device_id || !API) return;
  const body = {
    device_id,
    past_story_viewed:
      overrides.past_story_viewed !== undefined
        ? overrides.past_story_viewed
        : localStorage.getItem('past_story_viewed') === 'true',
    free_story_used:
      overrides.free_story_used !== undefined
        ? overrides.free_story_used
        : localStorage.getItem('free_story_used') === 'true',
    landing_flow:
      overrides.landing_flow !== undefined
        ? overrides.landing_flow
        : localStorage.getItem('landing_flow') === 'true',
  };
  try {
    await fetch(`${API}/api/story-state`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (e) {
    // Silent — server-side sync is best-effort; LS remains source of truth in the meantime.
  }
}

/**
 * Pull DEVICE PROFILE from server on app load. Full state hydration:
 *  - gate flags (past_story_viewed, free_story_used)
 *  - linked user info (user_id, email, phone, auth_provider)
 *  - account state (has_birth_details, is_premium)
 *
 * On reinstall / localStorage clear: server is source of truth and hydrates LS.
 * Returns the full DeviceProfile object so AppEntry can route properly.
 */
export async function syncStoryStateFromServer() {
  const device_id = await getDeviceId();
  if (!device_id || !API) return null;
  try {
    const res = await fetch(`${API}/api/story-state/${encodeURIComponent(device_id)}`);
    if (!res.ok) return null;
    const state = await res.json();

    const lsPsv = localStorage.getItem('past_story_viewed') === 'true';
    const lsFsu = localStorage.getItem('free_story_used') === 'true';

    // === Gate flags: server one-way latch (true overrides false) ===
    if (state.past_story_viewed && !lsPsv) {
      localStorage.setItem('past_story_viewed', 'true');
    }
    if (state.free_story_used && !lsFsu) {
      localStorage.setItem('free_story_used', 'true');
    }
    if ((lsPsv && !state.past_story_viewed) || (lsFsu && !state.free_story_used)) {
      pushStoryStateToServer();
    }

    // === Restore linked-account hints (for login prefill + routing) ===
    if (state.has_linked_account) {
      try {
        localStorage.setItem('device_linked_user_id', state.user_id || '');
        if (state.user_email) localStorage.setItem('device_linked_email', state.user_email);
        if (state.user_phone) localStorage.setItem('device_linked_phone', state.user_phone);
        if (state.user_name) localStorage.setItem('device_linked_name', state.user_name);
        if (state.auth_provider) localStorage.setItem('device_linked_provider', state.auth_provider);
        // Flag: returning user — AppEntry should route to /login (prefilled) instead of onboarding
        localStorage.setItem('returning_user', 'true');
      } catch (e) {}
    }

    if (state.has_birth_details) {
      localStorage.setItem('device_has_birth_details', 'true');
    }
    if (state.is_premium) {
      localStorage.setItem('is_premium', 'true');
      localStorage.setItem('isPremium', 'true');
    }

    return state;
  } catch (e) {
    return null;
  }
}

/** Marks past story viewed both in LS AND on server. Called on explicit forward actions. */
export function markPastStoryViewed() {
  try {
    localStorage.setItem('past_story_viewed', 'true');
    localStorage.setItem('free_story_used', 'true');
    localStorage.removeItem('watching_story');
    localStorage.removeItem('landing_flow');
  } catch (e) {}
  try {
    window.Capacitor?.Plugins?.Preferences?.set({ key: 'past_story_viewed', value: 'true' });
  } catch (e) {}
  pushStoryStateToServer({ past_story_viewed: true, free_story_used: true, landing_flow: false });
}
